/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {KTCard, KTCardBody, Select, Loading, Datepicker} from '../../../components'

import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {userTaskGetId, userTaskUpdate} from '../../../redux/slices/usertask.slice'
import {basicLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  UserTaskStatus: state.lookup.UserTaskStatus,
  TaskPriority: state.lookup.TaskPriority,
  loading: state.lookup.loading,
})

const selectUserTaskData = (state) => ({
  isRedirect: state.userTask.isRedirect,
  detail: state.userTask.detail,
  loading: state.userTask.loading,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  const {UserTaskStatus, TaskPriority, loading} = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectUserTaskData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(userTaskGetId(id))
    dispatch(basicLookup('UserTaskStatus'))
    dispatch(basicLookup('TaskPriority'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskPriorityId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: '',
    Name: '',
    TaskDate: new Date(),
    UserTaskStatusId: '',
    TaskPriorityId: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      const result = await dispatch(userTaskUpdate(values))
      if (userTaskUpdate.rejected.match(result)) {
        setSubmitting(false)
      }
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current) {
      const populatedValues = {
        Id: detail.id || '',
        Name: detail.name || '',
        TaskDate: detail.taskDate ? new Date(detail.taskDate) : new Date(),
        UserTaskStatusId: detail.userTaskStatusId || '',
        TaskPriorityId: detail.taskPriorityId || '',
      }

      formik.setValues(populatedValues)
      isFormPopulatedRef.current = true
    }
  }, [detail, formik])

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.USERTASK'}),
        path: '/user/task',
        isSeparator: false,
        isActive: false,
      },
      {
        title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
        path: `/user/task/detail/${id}`,
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl, id]
  )

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.USERTASK.EDIT'})}
      </PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form
              className='form'
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                  e.preventDefault()
                }
              }}
              onSubmit={formik.handleSubmit}
              noValidate
            >
              {detail?.userTaskAuthorizationTypeName && (
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2'>
                    {intl.formatMessage({id: 'USERTASK.GET.AUTHORIZATIONTYPENAME'})}
                  </label>
                  <div className='col-lg-6 fv-row'>
                    <span className='form-control form-control-solid mb-3 mb-lg-0'>
                      {detail.userTaskAuthorizationTypeName}
                    </span>
                  </div>
                </div>
              )}

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'USERTASK.CREATE.NAME'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Name')}
                  />
                  {formik.touched.Name && formik.errors.Name && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'USERTASK.CREATE.TASKDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('TaskDate')}
                    onChange={([date]) => formik.setFieldValue('TaskDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'USERTASK.CREATE.USERTASKSTASUSID'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={UserTaskStatus}
                    required
                    {...formik.getFieldProps('UserTaskStatusId')}
                    onChange={(item) => formik.setFieldValue('UserTaskStatusId', item.value)}
                  />
                  {formik.touched.UserTaskStatusId && formik.errors.UserTaskStatusId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.UserTaskStatusId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'USERTASK.CREATE.TASKPRIORITYID'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Select
                    data={TaskPriority}
                    {...formik.getFieldProps('TaskPriorityId')}
                    onChange={(item) => formik.setFieldValue('TaskPriorityId', item.value)}
                  />
                  {formik.touched.TaskPriorityId && formik.errors.TaskPriorityId && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.TaskPriorityId}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    type='button'
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {(loading || updateLoading) && <Loading />}
    </>
  )
}
export {Edit}
